import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BrandStrip } from "../components/BrandStrip";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { KpiTile } from "../components/KpiTile";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { Skel } from "../components/Skel";
import { SkelTable } from "../components/skeleton/SkelTable";
import { StatusPill } from "../components/StatusPill";
import type { FacilityDashboardAsset } from "@bitacorapro/api-types";
import { useFacilityDashboard, useRecipe } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { useQueryView } from "../lib/loading/useQueryView";
import { computeFacilityKpis, kpiHrefs, type FacilityKpis } from "../lib/facility/kpis";
import { CRIT_SEVERITY } from "../lib/facility/observacionesList";
import { threadWords } from "../lib/postventa/words";
import {
  daysSince,
  deriveStoreStatus,
  fmtDate,
  selectIdleStores,
  storeSubtitle,
  storeTitle,
} from "../lib/util";

type Layout = "exec" | "dense";

type EnrichedAsset = FacilityDashboardAsset & { status: ReturnType<typeof deriveStoreStatus> };

function enrichAsset(a: FacilityDashboardAsset, idleAfterDays: number | undefined): EnrichedAsset {
  return {
    ...a,
    status: deriveStoreStatus({
      openFindingsCount: a.openFindingsCount,
      lastVisit: a.lastVisit,
      idleAfterDays,
    }),
  };
}

// `skeletonShown`: OverviewRoute already painted this same skeleton while
// the recipe resolved. The dashboard query only starts once Overview
// mounts, so without it the frame would blink (visible → hidden for the
// 180 ms delay → visible) and announce «Cargando…» a second time.
export function Overview({ skeletonShown = false }: { skeletonShown?: boolean }) {
  const dashboardQuery = useFacilityDashboard();
  const { data } = dashboardQuery;
  const view = useQueryView(dashboardQuery);
  const { data: recipe } = useRecipe();
  const idleAfterDays = recipe?.alDiaThresholdDays;
  const [brand, setBrand] = useState<string>("all");
  // TODO(#dashboard-cleanup): re-enable when product confirms data-dense surface
  // Cast widens to the Layout union so dense-branch type-checks survive
  // the hard-pin without TS complaining about unreachable comparisons.
  const layout = "exec" as Layout;

  const allEnriched = useMemo(
    () => data?.byAsset.map((a) => enrichAsset(a, idleAfterDays)) ?? [],
    [data, idleAfterDays]
  );

  const enriched = useMemo(
    () => (brand === "all" ? allEnriched : allEnriched.filter((a) => a.brandId === brand)),
    [allEnriched, brand]
  );

  const brandOptions = useMemo(() => {
    const map = new Map<
      string,
      { id: string; name: string; count: number; logoUrl: string | null }
    >();
    for (const a of allEnriched) {
      if (!a.brandId || !a.brandName) continue;
      const entry = map.get(a.brandId);
      if (entry) entry.count += 1;
      else
        map.set(a.brandId, {
          id: a.brandId,
          name: a.brandName,
          count: 1,
          logoUrl: a.brandLogoUrl,
        });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [allEnriched]);

  // Each linked tile's number comes from the same predicate as the list it
  // opens (lib/facility/kpis.ts), so tile and table always agree.
  const kpis = useMemo(
    () => (data ? computeFacilityKpis(data, recipe, Date.now(), brand) : null),
    [data, recipe, brand]
  );

  const hrefs = kpis ? kpiHrefs(kpis, brand) : null;
  const coverageHref = hrefs?.coverage;
  const openHref = hrefs?.open;
  const critHref = hrefs?.crit;

  const criticalStores = useMemo(
    () =>
      enriched
        .filter((a) => a.status === "crit")
        .sort((a, b) => b.openFindingsCount - a.openFindingsCount),
    [enriched]
  );

  const idleStores = useMemo(() => selectIdleStores(enriched), [enriched]);

  const watchlist = useMemo(
    () =>
      enriched
        .filter((a) => a.status === "crit" || a.status === "warn" || a.status === "idle")
        .sort((a, b) => {
          const rank: Record<string, number> = { crit: 0, warn: 1, idle: 2, ok: 3 };
          return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
        }),
    [enriched]
  );

  const retry = () => void dashboardQuery.refetch();

  // The frame (header) lands at once on every branch; only the body
  // below it skeletonizes, errors or fills (handoff §11).
  if (view.kind === "error") {
    return (
      <div className="page">
        <OverviewHeader />
        <div className="panel">
          {view.offline ? (
            <PageError
              title="Sin conexión"
              description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              onRetry={view.retry}
            />
          ) : (
            <PageError
              title="No pudimos cargar el dashboard"
              description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
              onRetry={view.retry}
            />
          )}
        </div>
      </div>
    );
  }

  // The query is always enabled, so `disabled` never happens today; if it
  // ever does, nothing is on its way — say so instead of a skeleton forever.
  if (view.kind === "empty" || view.kind === "disabled") {
    return (
      <div className="page">
        <OverviewHeader />
        <div className="panel">
          <EmptyState
            title="Sin datos para mostrar"
            description="Cuando haya reportes en tus tiendas, el resumen aparece aquí."
          />
        </div>
      </div>
    );
  }

  // `ready` always carries data, so `kpis` is set; the guard only narrows
  // the type — the skeleton below is the `loading` branch.
  if (view.kind !== "ready" || kpis === null) {
    const visible = view.kind === "loading" ? view.visible || skeletonShown : true;
    return (
      <div className="page">
        <OverviewHeader pending skelVisible={visible} />
        <PageSkeleton label="Cargando el dashboard…" visible={visible} announce={!skeletonShown}>
          <OverviewSkeleton idleAfterDays={idleAfterDays ?? 30} />
        </PageSkeleton>
        {view.kind === "loading" && view.slow && (
          <div className="notice-banner notice-banner--action">
            <span>
              <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
              intentando.
            </span>
            <button type="button" className="btn btn--secondary btn--sm" onClick={retry}>
              Reintentar
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <OverviewHeader sub={summaryLine(kpis)} />

      {view.refreshFailed && (
        <div className="notice-banner notice-banner--action">
          <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
          <button type="button" className="btn btn--secondary btn--sm" onClick={retry}>
            Reintentar
          </button>
        </div>
      )}
      {view.revalidatingHint && <p className="refresh-hint">Actualizando…</p>}

      <BrandStrip
        brands={brandOptions}
        value={brand}
        onChange={setBrand}
        countLabel="tiendas"
        totalCount={allEnriched.length}
      />

      {/* KPI tier */}
      <div className={layout === "dense" ? "kpi-grid kpi-grid--6" : "kpi-grid"}>
        <KpiTile
          label="Tiendas con reporte, últimos 30 días"
          value={kpis.coverage}
          unit="%"
          {...(layout === "exec"
            ? { hint: `${kpis.visitedRecently} de ${kpis.total} tiendas con reporte ≤ 30 días` }
            : {})}
          dense={layout === "dense"}
          accent={layout === "exec"}
          {...(coverageHref ? { to: coverageHref } : {})}
        />
        <KpiTile
          label="Observaciones abiertas"
          value={kpis.openObs}
          {...(layout === "exec"
            ? {
                hint: `${kpis.sevCounts[CRIT_SEVERITY] ?? 0} críticas · ${kpis.sevCounts["moderada"] ?? 0} moderadas`,
              }
            : {})}
          dense={layout === "dense"}
          {...(openHref ? { to: openHref } : {})}
        />
        <KpiTile
          label="Críticas sin atender"
          value={kpis.critWithoutSla}
          {...(layout === "exec"
            ? {
                hint: `Críticas abiertas sin ${threadWords(recipe).assigneeLower} o con el plazo vencido`,
              }
            : {})}
          dense={layout === "dense"}
          {...(critHref ? { to: critHref } : {})}
        />
        <KpiTile
          label="Score global"
          value={kpis.avgScore !== null ? kpis.avgScore.toFixed(2) : "—"}
          unit="/ 5"
          dense={layout === "dense"}
        />
        {layout === "dense" && (
          <>
            <KpiTile
              label="Cierre prom."
              value={kpis.avgClose !== null ? kpis.avgClose.toFixed(1) : "—"}
              unit="días"
              dense
            />
            <KpiTile label="Tiendas críticas" value={criticalStores.length} dense />
          </>
        )}
      </div>

      {/* Second row depends on layout */}
      {layout === "exec" ? (
        <ExecLayout idleStores={idleStores} idleAfterDays={idleAfterDays ?? 30} />
      ) : (
        <DenseLayout watchlist={watchlist} />
      )}
    </div>
  );
}

// ── Frame + skeleton (shared with OverviewRoute) ───────────────────

// The page header. The greeting needs no data, so it always renders for
// real; only the summary line waits on the KPIs. `pending` keeps a text
// bar in the real line box (hidden with the body during the skeleton
// delay); no `sub` drops the line (error / empty). `neutral` is for the
// window before the vertical is known (OverviewRoute): the «Dashboard ·»
// eyebrow is facility copy, so it becomes a bar in the same line box
// while pending, and is dropped on the error frame.
export function OverviewHeader({
  sub,
  pending = false,
  skelVisible = true,
  neutral = false,
}: {
  sub?: string;
  pending?: boolean;
  skelVisible?: boolean;
  neutral?: boolean;
}) {
  const session = useSession();
  const firstName = session?.user.displayName.split(" ")[0] ?? "";
  const greeting = greetingForNow();
  const now = new Date();
  return (
    <div className="page-header">
      <div className="page-header__lead">
        {neutral ? (
          pending && (
            <div className="page-header__eyebrow" data-skel-visible={String(skelVisible)}>
              <Skel variant="text" width={140} />
            </div>
          )
        ) : (
          <div className="page-header__eyebrow">
            Dashboard · {monthLabel(now)} {now.getFullYear()}
          </div>
        )}
        <h1 className="page-header__title">
          {greeting}
          {firstName ? `, ${firstName}` : ""}
        </h1>
        {pending ? (
          <p className="page-header__sub" data-skel-visible={String(skelVisible)}>
            <Skel variant="text" width="55%" />
          </p>
        ) : (
          sub !== undefined && <p className="page-header__sub">{sub}</p>
        )}
      </div>
      <div className="page-header__actions">
        {/* TODO(#dashboard-cleanup): re-enable when product confirms data-dense surface */}
        {/* <LayoutToggle value={layout} onChange={setLayout} /> */}
        <Link to="/ia" className="btn btn--primary">
          <Icon.Sparkles s={16} /> Preguntar a la IA
        </Link>
      </div>
    </div>
  );
}

// The exec KPI band's static labels, and whether each tile carries a hint
// line — the skeleton draws the same tiles so the real ones land in place.
const KPI_SKELETON: { label: string; hint: boolean }[] = [
  { label: "Tiendas con reporte, últimos 30 días", hint: true },
  { label: "Observaciones abiertas", hint: true },
  { label: "Críticas sin atender", hint: true },
  { label: "Score global", hint: false },
];

// The body skeleton: the real `.kpi-grid` tiles (labels are static copy,
// so they render for real) and the «Sin reporte reciente» panel with its
// compact table — the exec layout, the only one live today. The brand
// strip is left out: it only renders for orgs with 2+ brands, so drawing
// it would shift the page for everyone else. `idleAfterDays` is unknown
// while the recipe resolves (OverviewRoute) — the panel sub is then a bar.
// `labels={false}` is for that same window: the vertical isn't known yet
// and this route also lands postventa / unconfigured verticals, so no
// facility copy («Tiendas…», «Sin reporte reciente») may show — every
// label becomes a bar inside the same tile / panel boxes.
export function OverviewSkeleton({
  idleAfterDays,
  labels = true,
}: {
  idleAfterDays?: number;
  labels?: boolean;
}) {
  return (
    <>
      <div className="kpi-grid" aria-hidden="true">
        {KPI_SKELETON.map((tile, i) => (
          <div key={tile.label} className={i === 0 ? "kpi-tile kpi-tile--accent" : "kpi-tile"}>
            <div className="kpi-tile__label">
              {labels ? tile.label : <Skel variant="text" width="70%" />}
            </div>
            <div className="kpi-tile__value">
              <Skel variant="text" width="35%" />
            </div>
            {tile.hint && (
              <div className="kpi-tile__hint">
                <Skel variant="text" width="80%" />
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 20 }}>
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">
              {labels ? "Sin reporte reciente" : <Skel variant="text" width={160} />}
            </div>
            <div className="panel__sub mono">
              {labels && idleAfterDays !== undefined ? (
                <>Sin reportes o último &gt; {idleAfterDays} días</>
              ) : (
                <Skel variant="text" width={200} />
              )}
            </div>
          </div>
          {labels ? (
            <SkelTable rows={8} cols={2} headers={["Tienda", "Último reporte"]} compact />
          ) : (
            <SkelTable rows={8} cols={2} compact />
          )}
        </div>
      </div>
    </>
  );
}

// ── Layout sub-components ──────────────────────────────────────────

// Exec layout is a single panel today. "Desempeño por zona" and
// "Tiendas críticas" were hidden on founder feedback (2026-09-26): no
// current client has zonas, and the critical list duplicated the
// watchlist signal — the per-store data still lives in /tiendas.
function ExecLayout({
  idleStores,
  idleAfterDays,
}: {
  idleStores: EnrichedAsset[];
  idleAfterDays: number;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">Sin reporte reciente</div>
          <div className="panel__sub mono">Sin reportes o último &gt; {idleAfterDays} días</div>
        </div>
        <div className="panel__body--flush">
          {idleStores.length === 0 ? (
            <div className="empty">
              <div className="empty__title">Todas al día</div>
              <div className="empty__sub">
                Todas las tiendas tienen un reporte en los últimos {idleAfterDays} días.
              </div>
            </div>
          ) : (
            <table className="dtable dtable--compact">
              <thead>
                <tr>
                  <th>Tienda</th>
                  <th>Último reporte</th>
                </tr>
              </thead>
              <tbody>
                {idleStores.slice(0, 8).map((t) => {
                  const ago = daysSince(t.lastVisit);
                  return (
                    <StoreRow key={t.id} t={t}>
                      <td className="cell-meta">
                        {t.lastVisit === null
                          ? "Sin reportes"
                          : `${fmtDate(t.lastVisit)}${ago !== null ? ` · ${ago}d` : ""}`}
                      </td>
                    </StoreRow>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function DenseLayout({ watchlist }: { watchlist: EnrichedAsset[] }) {
  return (
    <div className="grid-23" style={{ marginBottom: 20 }}>
      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">Tiendas que requieren atención</div>
          <div className="panel__sub mono">{watchlist.length} con flag</div>
          <div className="panel__actions">
            <Link to="/tiendas" className="btn btn--ghost btn--sm">
              Ver tus tiendas <Icon.Arrow s={12} />
            </Link>
          </div>
        </div>
        <div className="panel__body--flush">
          {watchlist.length === 0 ? (
            <div className="empty">
              <div className="empty__title">Sin alertas en tus tiendas</div>
              <div className="empty__sub">Nada que requiera atención hoy.</div>
            </div>
          ) : (
            <table className="dtable dtable--compact">
              <thead>
                <tr>
                  <th>Tienda</th>
                  <th>Zona</th>
                  <th className="col-num">Score</th>
                  <th className="col-num">Observaciones abiertas</th>
                  <th>Último reporte</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {watchlist.slice(0, 10).map((t) => {
                  const ago = daysSince(t.lastVisit);
                  return (
                    <StoreRow key={t.id} t={t}>
                      <td>{t.brandName ?? "—"}</td>
                      <td className="col-num">
                        {t.score !== null ? `${t.score.toFixed(2)}/5.0` : "—"}
                      </td>
                      <td className="col-num">{t.openFindingsCount}</td>
                      <td className="cell-meta">
                        {fmtDate(t.lastVisit)}
                        {ago !== null && ` · ${ago}d`}
                      </td>
                      <td>
                        <StatusPill status={t.status} />
                      </td>
                    </StoreRow>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="panel panel--ia">
        <div className="panel__head">
          <div className="panel__title">
            <Icon.Sparkles s={14} /> Insights IA
          </div>
        </div>
        <div className="panel__body panel__body--ia-copy">
          <p>
            Los insights pre-armados de la IA aparecen aquí cuando el job{" "}
            <code>summarize-portfolio</code> esté integrado.
          </p>
          <Link to="/ia" className="btn btn--primary btn--sm">
            Abrir AI Insights
          </Link>
        </div>
      </div>
    </div>
  );
}

// Reusable table row. First column is a real <Link> to the store
// detail; the rest of the cells render whatever the caller passes
// via children. Keyboard/screen-reader friendly — a single
// focusable element per row (the link), no whole-row onClick hack.
function StoreRow({ t, children }: { t: EnrichedAsset; children: React.ReactNode }) {
  return (
    <tr>
      <td>
        <div className="cell-with-sub">
          <Link to={`/tiendas/${t.id}`} className="cell-strong cell-strong--link">
            {storeTitle(t)}
          </Link>
          <span className="cell-sub">
            {[storeSubtitle(t), t.brandName].filter(Boolean).join(" · ") || "—"}
          </span>
        </div>
      </td>
      {children}
    </tr>
  );
}

// ── Small UI helpers ──────────────────────────────────────────────

// TODO(#dashboard-cleanup): re-enable when product confirms data-dense surface
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for the re-enable above; remove together with the TODO
function LayoutToggle({ value, onChange }: { value: Layout; onChange: (l: Layout) => void }) {
  return (
    <div className="seg-toggle" role="group" aria-label="Cambiar layout">
      <button
        type="button"
        className={`seg-toggle__btn ${value === "exec" ? "seg-toggle__btn--active" : ""}`}
        onClick={() => onChange("exec")}
      >
        Ejecutivo
      </button>
      <button
        type="button"
        className={`seg-toggle__btn ${value === "dense" ? "seg-toggle__btn--active" : ""}`}
        onClick={() => onChange("dense")}
      >
        Data-dense
      </button>
    </div>
  );
}

function summaryLine(kpis: FacilityKpis): string {
  if (kpis.coverage < 70) {
    return `${kpis.coverage}% de las tiendas con reporte en los últimos 30 días — quedan tiendas sin reporte este mes.`;
  }
  return `Tus tiendas van a buen ritmo. ${kpis.openObs} observaciones abiertas pendientes de cierre.`;
}

function greetingForNow(): string {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString("es-CL", { month: "long" }).replace(/^./, (c) => c.toUpperCase());
}
